let idCounter = 0
export function nextRowKey() {
  idCounter += 1
  return idCounter
}

export function createPillar(existingPillars, x = 0, y = 0) {
  return { key: nextRowKey(), id: `P${existingPillars.length + 1}`, x, y, width: 0.3, depth: 0.3 }
}

export function createBeam(existingBeams, xI = 0, yI = 0, xJ = 0, yJ = 0) {
  return {
    key: nextRowKey(),
    id: `V${existingBeams.length + 1}`,
    xI,
    yI,
    xJ,
    yJ,
    width: 0.2,
    height: 0.5,
    E: 25000000,
    nu: 0.2,
  }
}

export function createSlab(existingSlabs, polygon = []) {
  return {
    key: nextRowKey(),
    id: `L${existingSlabs.length + 1}`,
    polygon,
    thickness: 0.12,
    E: 25000000,
    nu: 0.2,
    boundaryEdges: [],
  }
}

export function createAreaLoad(existingLoads, slab = '') {
  return { key: nextRowKey(), id: `Q${existingLoads.length + 1}`, type: 'area', slab, value: -4, polygon: null }
}

export function createLineLoad(existingLoads, beam = '') {
  return {
    key: nextRowKey(),
    id: `LN${existingLoads.length + 1}`,
    type: 'line',
    beam,
    start: '',
    end: '',
    value: -8,
  }
}

export function createPointLoad(existingLoads, x = 0, y = 0) {
  return { key: nextRowKey(), id: `C${existingLoads.length + 1}`, type: 'point', x, y, value: -10, Mx: 0, My: 0 }
}

export function createBoundaryEdge(existingEdges, edge = 0) {
  const nextId = (existingEdges.filter((e) => e.id.startsWith('B')).length || 0) + 1
  return {
    key: nextRowKey(),
    id: `B${nextId}`,
    edge,
    releaseMoment: false,
  }
}

// Seed matching the HANDOFF.md example: a 5x4 slab (L1), one beam along its edge (V1), two
// corner pillars (P1/P2), and one load of each type (area/line/point).
export const DEFAULT_SLABS = [
  {
    key: nextRowKey(),
    id: 'L1',
    polygon: [
      { x: 0, y: 0 },
      { x: 5, y: 0 },
      { x: 5, y: 4 },
      { x: 0, y: 4 },
    ],
    thickness: 0.12,
    E: 30000000,
    nu: 0.2,
    boundaryEdges: [],
  },
]

export const DEFAULT_BEAMS = [
  { key: nextRowKey(), id: 'V1', xI: 0, yI: 0, xJ: 5, yJ: 0, width: 0.2, height: 0.5, E: 30000000, nu: 0.2 },
]

export const DEFAULT_PILLARS = [
  { key: nextRowKey(), id: 'P1', x: 0, y: 0, width: 0.3, depth: 0.3 },
  { key: nextRowKey(), id: 'P2', x: 5, y: 0, width: 0.3, depth: 0.3 },
]

export const DEFAULT_LOADS = [
  { key: nextRowKey(), id: 'PP', type: 'area', slab: 'L1', value: -4, polygon: null },
  { key: nextRowKey(), id: 'ALV', type: 'line', beam: 'V1', start: 1, end: 4, value: -8 },
  { key: nextRowKey(), id: 'C1', type: 'point', x: 2.5, y: 2, value: -10, Mx: 0, My: 0 },
]

export const DEFAULT_MESH_MAX_SPACING = 1

function numOrUndefined(value) {
  if (value === '' || value === undefined || value === null) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

export function mapSlabsForApi(slabs) {
  return slabs.map((s) => {
    const slab = {
      id: s.id,
      polygon: s.polygon.map((p) => ({ x: Number(p.x), y: Number(p.y) })),
      thickness: Number(s.thickness) || 0,
      E: Number(s.E) || 0,
      nu: Number(s.nu) || 0,
    }
    if (Array.isArray(s.boundaryEdges) && s.boundaryEdges.length > 0) {
      slab.boundaryEdges = s.boundaryEdges
        .filter((e) => e.releaseMoment)
        .map((e) => ({
          id: e.id,
          edge: Number(e.edge),
          releaseMoment: true,
        }))
    }
    return slab
  })
}

export function mapBeamsForApi(beams) {
  return beams.map((b) => ({
    id: b.id,
    start: { x: Number(b.xI), y: Number(b.yI) },
    end: { x: Number(b.xJ), y: Number(b.yJ) },
    width: Number(b.width) || 0,
    height: Number(b.height) || 0,
    E: Number(b.E) || 0,
    nu: Number(b.nu) || 0,
  }))
}

export function mapPillarsForApi(pillars) {
  return pillars.map((p) => ({
    id: p.id,
    x: Number(p.x),
    y: Number(p.y),
    width: Number(p.width) || 0,
    depth: Number(p.depth) || 0,
  }))
}

export function mapLoadForApi(l) {
  if (l.type === 'area') {
    const load = { id: l.id, type: 'area', slab: l.slab, value: Number(l.value) || 0 }
    if (Array.isArray(l.polygon) && l.polygon.length >= 3) {
      load.polygon = l.polygon.map((p) => ({ x: Number(p.x), y: Number(p.y) }))
    }
    return load
  }
  if (l.type === 'line') {
    const load = { id: l.id, type: 'line', beam: l.beam, value: Number(l.value) || 0 }
    const start = numOrUndefined(l.start)
    const end = numOrUndefined(l.end)
    if (start !== undefined) load.start = start
    if (end !== undefined) load.end = end
    return load
  }
  return {
    id: l.id,
    type: 'point',
    x: Number(l.x),
    y: Number(l.y),
    value: Number(l.value) || 0,
    Mx: Number(l.Mx) || 0,
    My: Number(l.My) || 0,
  }
}

export function mapLoadsForApi(loads) {
  return loads.map(mapLoadForApi)
}

// Removing a slab also removes area loads that reference it.
export function cascadeRemoveSlab(slabKey, { slabs, loads }) {
  const removedSlab = slabs.find((s) => s.key === slabKey)
  const newSlabs = slabs.filter((s) => s.key !== slabKey)
  if (!removedSlab) {
    return { slabs: newSlabs, loads }
  }
  const newLoads = loads.filter((l) => !(l.type === 'area' && l.slab === removedSlab.id))
  return { slabs: newSlabs, loads: newLoads }
}

// Removing a beam also removes line loads that reference it.
export function cascadeRemoveBeam(beamKey, { beams, loads }) {
  const removedBeam = beams.find((b) => b.key === beamKey)
  const newBeams = beams.filter((b) => b.key !== beamKey)
  if (!removedBeam) {
    return { beams: newBeams, loads }
  }
  const newLoads = loads.filter((l) => !(l.type === 'line' && l.beam === removedBeam.id))
  return { beams: newBeams, loads: newLoads }
}

// Reassigns every pillar/beam/slab/load id to a contiguous P1../V1../L1../Q1../LN1../C1.. sequence
// (in their current array order), so ids never drift out of sync or collide after a create/delete
// — the "Id" field stays editable, but the next create/delete resets it back to sequential. Beam
// and slab ids are remapped in any load that references them (line loads carry a beam id, area
// loads a slab id) so those references stay valid across the rename. `key` (the stable React/
// selection identity) is untouched.
export function renumberModel({ pillars, beams, slabs, loads }) {
  const newPillars = pillars.map((p, i) => ({ ...p, id: `P${i + 1}` }))

  const beamIdMap = {}
  const newBeams = beams.map((b, i) => {
    const id = `V${i + 1}`
    beamIdMap[b.id] = id
    return { ...b, id }
  })

  const slabIdMap = {}
  const newSlabs = slabs.map((s, i) => {
    const id = `L${i + 1}`
    slabIdMap[s.id] = id
    const newSlab = { ...s, id }
    if (Array.isArray(s.boundaryEdges)) {
      newSlab.boundaryEdges = s.boundaryEdges.map((e, ei) => ({
        ...e,
        id: `B${i + 1}_${ei + 1}`,
      }))
    }
    return newSlab
  })

  let areaCount = 0
  let lineCount = 0
  let pointCount = 0
  const newLoads = loads.map((l) => {
    if (l.type === 'area') {
      areaCount += 1
      return { ...l, id: `Q${areaCount}`, slab: slabIdMap[l.slab] ?? l.slab }
    }
    if (l.type === 'line') {
      lineCount += 1
      return { ...l, id: `LN${lineCount}`, beam: beamIdMap[l.beam] ?? l.beam }
    }
    pointCount += 1
    return { ...l, id: `C${pointCount}` }
  })

  return { pillars: newPillars, beams: newBeams, slabs: newSlabs, loads: newLoads }
}
